import { Box } from "@mui/material";
import { getIntakeOptions } from "@platform/api/golden";
import { Button, Card, Link, Typography } from "@platform/design-system";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router";

import { useGoldenClient } from "../api/GoldenClientProvider";

import { SectionStatus } from "./SectionStatus";

import type { ReactNode } from "react";

export const CASE_INTAKE_TEST_ID = "golden-case-intake";
const INTAKE_ANCHOR = "intake";

// Mounted only once the reviewer asks for the intake, which can be large.
const IntakeData = ({ caseId }: { readonly caseId: number }): ReactNode => {
  const client = useGoldenClient();
  const intake = useQuery(
    getIntakeOptions({ client, path: { case_id: caseId } }),
  );

  if (!intake.isSuccess) {
    return (
      <SectionStatus
        testId={CASE_INTAKE_TEST_ID}
        label="intake data"
        error={intake.error}
      />
    );
  }
  const { run_id: runId, data } = intake.data;
  return (
    <Box>
      <Typography
        variant="body-medium"
        data-testid={`${CASE_INTAKE_TEST_ID}-run`}
        sx={{ mb: 1 }}
      >
        From{" "}
        <Link component={RouterLink} to={`/cases/${caseId}/runs/${runId}`}>
          run #{runId}
        </Link>
        , the case&apos;s latest.
      </Typography>
      <Box
        component="pre"
        data-testid={`${CASE_INTAKE_TEST_ID}-data`}
        sx={{
          m: 0,
          p: 2,
          bgcolor: "action.hover",
          overflowX: "auto",
          fontSize: 13,
        }}
      >
        {JSON.stringify(data, null, 2)}
      </Box>
    </Box>
  );
};

/** What RUMI's latest run of the case started from, for deciding whether the intake held an answer. */
export const CaseIntake = ({
  caseId,
  hasBaseline,
}: {
  readonly caseId: number;
  readonly hasBaseline: boolean;
}): ReactNode => {
  const [shown, setShown] = useState(false);
  const section = useRef<HTMLDivElement>(null);
  const { hash } = useLocation();

  // The review queue links here as /cases/:id#intake; the router does not scroll to a
  // hash, and the section renders only once the case has loaded.
  useEffect(() => {
    if (hash === `#${INTAKE_ANCHOR}`) section.current?.scrollIntoView();
  }, [hash]);

  const body = (): ReactNode => {
    if (!hasBaseline) {
      return (
        <Typography variant="body-medium" color="textSecondary">
          No baseline yet.
        </Typography>
      );
    }
    if (shown) return <IntakeData caseId={caseId} />;
    return (
      <Box>
        <Button
          variant="secondary"
          data-testid={`${CASE_INTAKE_TEST_ID}-show`}
          onClick={() => setShown(true)}
        >
          Show intake data
        </Button>
      </Box>
    );
  };

  return (
    <Card
      ref={section}
      id={INTAKE_ANCHOR}
      data-testid={CASE_INTAKE_TEST_ID}
      title="Intake data"
    >
      <Typography variant="body-medium" color="textSecondary" sx={{ mb: 2 }}>
        What RUMI started from on the case&apos;s latest run: the broker&apos;s
        intake as the RFP stood when the run copied it, without the
        analyst&apos;s plans, rates, or notes. Use it, with the documents, to
        decide whether the intake contained an answer.
      </Typography>
      {body()}
    </Card>
  );
};
